import { addDays, format, subDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

const DATE_FORMAT = 'yyyy-MM-dd';

type TargetEventDate = {
  milliseconds: number;
  /**
   * We must adjust the date to the event's timezone so that the search results
   * are accurate (and not based on user's local timezone)
   */
  timezone: string;
};

type GetProposedBookingDatesArgs = {
  /**
   * How much earlier the checkin date should be compared to the finalized event date.
   *
   * @default 1
   */
  checkinDelta?: number;

  /**
   * How much later the checkout date should be compared to the finalized event date.
   *
   * @default 1
   */
  checkoutDelta?: number;
};

type GetProposedBookingDatesReturn = {
  /**
   * The propose checkin date -- formatted as 'yyyy-MM-dd';
   *
   * example -> '2025-11-27'
   */
  checkin: string;

  /**
   * The propose checkout date -- formatted as 'yyyy-MM-dd';
   *
   *  example -> '2025-11-29'
   */
  checkout: string;
};

/**
 * Get the proposed booking dates for a finalized event date.
 *
 * @param finalizedEventDate - The finalized event date.
 * @returns The proposed checkin and checkout dates
 *
 * @example
 * finalizedEventDate = { milliseconds: 1764316800000, timezone: 'America/Los_Angeles' }
 *
 * returns -> { checkin: '2025-11-27', checkout: '202-11-29' }
 */
export const getProposedBookingDates = (finalizedEventDate: TargetEventDate, overrides: GetProposedBookingDatesArgs = {}): GetProposedBookingDatesReturn => {
  const { checkinDelta = 1, checkoutDelta = 1 } = overrides;
  const eventDateWithTimezone = utcToZonedTime(finalizedEventDate.milliseconds, finalizedEventDate.timezone);
  return {
    checkin: format(subDays(eventDateWithTimezone, checkinDelta), DATE_FORMAT),
    checkout: format(addDays(eventDateWithTimezone, checkoutDelta), DATE_FORMAT)
  };
};
